import {
  getPluginAdminNavEntries,
  getSearchPage,
  PLUGIN_ADMIN_NAV_GROUPS,
} from '../../../app/routes/searchPageMap';

const createItem = (
  name,
  linkKey,
  icon,
  disabled = false,
  testId = null,
  hiddenInHorizontalNav = false,
) => ({
  name,
  type: 'item',
  linkKey,
  icon,
  disabled,
  testId,
  hiddenInHorizontalNav,
});

const createCollapse = (name, children, icon, disabled = false, testId = null) => ({
  name,
  type: 'collapse',
  icon,
  children,
  disabled,
  testId,
});

const createSection = (name, children) => ({
  name,
  type: 'section',
  children,
});

const SIDEBAR = 'sidebar.';
const SETTINGS = '.settings';
const ANALYTICS = '.analytics';
const CUSTOMIZATION = '.customization';
const SEARCH = `${SIDEBAR}search`;
const RECS = `${SIDEBAR}recs`;
const STORE = `${SIDEBAR}store`;
const ACCOUNT = `${SIDEBAR}account`;

const SEARCH_EXPERIENCE_ALIASES = {
  unified: 'unified',
  widget: 'unified',
  search_widget: 'unified',
  searchWidget: 'unified',
  classic: 'classic',
  fullpage: 'fullpage',
  full_page: 'fullpage',
};

const UI_SETTINGS_BY_EXPERIENCE = {
  unified: ['searchBoxSettings', 'resultsPageSettings'],
  classic: ['searchBoxSettings', 'resultsPageSettings'],
  fullpage: ['searchBoxSettings', 'resultsPageSettings'],
};

const normalizeSearchExperience = (mode) => {
  if (!mode || typeof mode !== 'string') return null;
  return SEARCH_EXPERIENCE_ALIASES[mode] || null;
};

const getSearchSurfaceLabelKey = (entry, searchMode) => {
  if (entry.id !== 'searchBoxSettings') return entry.navName;
  if (!searchMode) return entry.navName;
  if (normalizeSearchExperience(searchMode) === 'classic') {
    return 'sidebar.search.uiSettings.searchDropdown';
  }
  return 'sidebar.search.uiSettings.searchWidget';
};

/**
 * Generate settings menu items based on available views from production config
 * @param {Array} availableViews - Array of view keys from search experience config (e.g., ['searchBox', 'resultsPage'])
 * @param {boolean} anySearchEnabled - Whether any search feature is enabled
 * @returns {Array} Array of menu items
 */
const getSearchSettingsItems = (
  availableViews = [],
  anySearchEnabled,
  capabilities = {},
  productId = 'plugin',
) => {
  const create = (name, linkKey, hiddenInHorizontalNav = false) => createItem(
    `${SEARCH}${SETTINGS}.${name}`,
    linkKey,
    null,
    !anySearchEnabled,
    null,
    hiddenInHorizontalNav,
  );

  const settingsV2 = getSearchPage('settingsV2');
  const baseItems = [
    productId !== 'cloud' && capabilities.settingsV2 && createItem(
      settingsV2.navName,
      settingsV2.linkKey,
      settingsV2.navIcon,
      false,
      settingsV2.menuTestId,
    ),
    create('data', 'searchSettingsData'),
  ].filter(Boolean);

  // Dynamic menu based on available views
  const settingsItems = [];

  const hasSearchBoxSurface = availableViews.some(
    view => ['searchBox', 'dropdown', 'searchDropdown'].includes(view),
  );
  if (hasSearchBoxSurface) {
    settingsItems.push(create('searchbox', 'searchSettingsSearchbox'));
  }
  if (availableViews.includes('resultsPage')) {
    settingsItems.push(create('result-page', 'searchSettingsResultPage'));
  }
  settingsItems.push(create('theme', 'searchSettingsTheme'));

  return [...baseItems, ...settingsItems];
};

export const buildSidebar = ({
  // eslint-disable-next-line no-unused-vars
  shopId: _shopId,
  onboarding,
  searchEnabled,
  recsEnabled,
  anySearchEnabled,
  anyRecsEnabled,
  isLoaded,
  experimentIsDisabled,
  // eslint-disable-next-line no-unused-vars
  platformSettings: _platformSettings,
  availableViews = [],
  searchMode = null,
  capabilities = {},
  productId = 'plugin',
  interfaceManagedExternally = false,
}) => {
  const create = (prefix, name, linkKey, icon, disabled) => createItem(`${prefix}${name}`, linkKey, icon, disabled);

  const searchChildren = [
    create(SEARCH, '.home', 'home', 'home', !anySearchEnabled),
    !onboarding.search.completed && searchEnabled
    && create(SEARCH, '.onboarding', 'searchOnboarding', 'boat', !searchEnabled),
    create(SEARCH, '.dashboard', 'searchDashboard', 'trending-up', !anySearchEnabled),
    createCollapse(`${SEARCH}${ANALYTICS}`, [
      create(SEARCH, `${ANALYTICS}.queryPopular`, 'searchQueryPopular', null, !anySearchEnabled),
      create(SEARCH, `${ANALYTICS}.lostOpportunities`, 'searchLostOpportunities', null, !anySearchEnabled),
      create(SEARCH, `${ANALYTICS}.queryNoResult`, 'searchQueryNoResult', null, !anySearchEnabled),
    ], 'chart'),
    // create(SEARCH, `${CUSTOMIZATION}`, 'searchCustomization', 'code-setting', !anySearchEnabled),
    createCollapse(`${SEARCH}${CUSTOMIZATION}`, [
      create(SEARCH, `${CUSTOMIZATION}.fields`, 'searchCustomizationFields', null, !anySearchEnabled),
      create(SEARCH, `${CUSTOMIZATION}.filters`, 'searchCustomizationFilters', null, !anySearchEnabled), // New menu item
      create(SEARCH, `${CUSTOMIZATION}.synonyms`, 'searchCustomizationSynonyms', null, !anySearchEnabled), // New menu item
    ], 'code-setting'),
    createCollapse(
      `${SEARCH}${SETTINGS}`,
      getSearchSettingsItems(availableViews, anySearchEnabled, capabilities, productId),
      'settings',
    ),
  ].filter(Boolean);

  const recsChildren = [
    !onboarding.recs.completed && recsEnabled
    && create(RECS, '.onboarding', 'recsOnboarding', 'boat', !recsEnabled),
    create(RECS, '.dashboard', 'recsDashboard', 'trending-up', !anyRecsEnabled),
    createCollapse(`${RECS}${ANALYTICS}`, [
      create(RECS, `${ANALYTICS}.widgets`, 'recsAnalyticsWidgets', null, !anyRecsEnabled),
    ], 'chart'),
    createCollapse(`${RECS}${CUSTOMIZATION}`, [
      create(RECS, `${CUSTOMIZATION}.fields`, 'recsCustomizationFields', null, !anyRecsEnabled),
    ], 'code-setting'),
    createCollapse(`${RECS}${SETTINGS}`, [
      create(RECS, `${SETTINGS}.general`, 'recsSettingsGeneral', null, !anyRecsEnabled),
      create(RECS, `${SETTINGS}.theme`, 'recsSettingsTheme', null, !anyRecsEnabled),
    ], 'settings'),
  ].filter(Boolean);

  const storeChildren = [
    create(SEARCH, '.catalog', 'storeProducts', 'link', false),
    createCollapse(`${STORE}${ANALYTICS}`, [
      create(SEARCH, `${ANALYTICS}.popularProducts`, 'storePopularProducts', null, !isLoaded),
      create(SEARCH, `${ANALYTICS}.conversion`, 'storeConversion', null, !isLoaded),
    ], 'chart'),
    create(STORE, '.experiment', 'storeExperiment', 'link', experimentIsDisabled()),
  ].filter(Boolean);

  const accountChildren = [
    create(ACCOUNT, '.settings', 'accountSettings', 'settings'),
    create(ACCOUNT, '.logout', 'accountLogout', 'sign-in'),
  ];

  // -------------------------------------------------------------------------
  // Plugin admin: search-only production nav (derived from searchPageMap contract)
  // -------------------------------------------------------------------------
  // Top-level items, analytics collapse, and settings collapse are all derived
  // from SEARCH_PAGE_MAP entries tagged with navGroup.
  // Capability gating uses entry.requiresCapability directly — no manual duplication.
  // -------------------------------------------------------------------------
  const navEntries = getPluginAdminNavEntries(productId);
  const normalizedSearchMode = normalizeSearchExperience(searchMode);
  const isManagedInterfacePage = (entry) => [
    'settingsV2',
    'theme',
    'searchBoxSettings',
    'resultsPageSettings',
    'searchMode',
    'cardTemplate',
  ].includes(entry.id);

  const entryMatchesViews = (entry) => {
    if (!Array.isArray(entry.requiredViews) || entry.requiredViews.length === 0) return true;
    if (normalizedSearchMode && UI_SETTINGS_BY_EXPERIENCE[normalizedSearchMode]) {
      return UI_SETTINGS_BY_EXPERIENCE[normalizedSearchMode].includes(entry.id);
    }
    if (!Array.isArray(availableViews) || availableViews.length === 0) return true;
    return entry.requiredViews.some((view) => availableViews.includes(view));
  };

  const toItem = (entry) => {
    const isDisabled = (entry.requiresCapability
      ? !Boolean(capabilities[entry.requiresCapability])
      : false);
    return createItem(
      getSearchSurfaceLabelKey(entry, normalizedSearchMode),
      entry.linkKey,
      entry.navIcon,
      isDisabled,
      entry.menuTestId,
      productId === 'cloud' && entry.hiddenInCloudHorizontalNav,
    );
  };

  const toCollapse = (groupKey, entries) => {
    const group = PLUGIN_ADMIN_NAV_GROUPS[groupKey];
    const children = entries
      .filter(entryMatchesViews)
      .filter(entry => !(productId === 'cloud' && entry.id === 'diagnostics'))
      .filter(entry => !(productId === 'cloud'
        && interfaceManagedExternally
        && isManagedInterfacePage(entry)))
      .filter(entry => entry.requiresCapability !== 'merchandising_bridge_v1'
        || Boolean(capabilities.merchandising_bridge_v1))
      .filter(entry => entry.requiresCapability !== 'descriptionParametersV1'
        || capabilities.descriptionParametersV1 === true)
      .map(toItem);
    if (productId === 'cloud' && interfaceManagedExternally && children.length === 0) return null;
    const groupDisabled = children.length > 0 && children.every(child => child.disabled);
    return createCollapse(
      group.name,
      children,
      group.icon,
      groupDisabled,
      group.testId,
    );
  };

  const pluginAdminChildren = productId === 'cloud' ? [
    ...navEntries.top.map(toItem),
    toCollapse('analytics', navEntries.analytics),
    navEntries.merchandising.length > 0 && toCollapse('merchandising', navEntries.merchandising),
    toCollapse('customization', navEntries.customization),
    toCollapse('uiSettings', navEntries.uiSettings),
    toCollapse('settings', navEntries.settings),
  ].filter(Boolean) : [
    ...navEntries.top.map(toItem),
    toCollapse('analytics', navEntries.analytics),
    toCollapse('settings', navEntries.settings),
    navEntries.merchandising.length > 0 && toCollapse('merchandising', navEntries.merchandising),
    toCollapse('uiSettings', navEntries.uiSettings),
  ].filter(Boolean);

  return {
    search: createSection(`${SIDEBAR}section.search`, searchChildren),
    recs: createSection(`${SIDEBAR}section.recs`, recsChildren),
    store: createSection(`${SIDEBAR}section.store`, storeChildren),
    account: createSection(ACCOUNT, accountChildren),
    pluginAdmin: createSection(`${SIDEBAR}section.search`, pluginAdminChildren),
  };
};
